import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from '../routes/ids.js';
export const PORTFOLIO_PERFORMANCE_QUERY_ID = 'query.data.portfolioPerformance.list';
export const PORTFOLIO_PERFORMANCE_TABLE_ID = 'table.data.performance';
const tk = (value) => value;
const telemetry = (value) => value;
function metadata(id, module, feature) {
    return {
        id,
        schemaVersion: 1,
        module,
        feature,
        privacyClassification: 'FINANCIAL',
        telemetryId: telemetry(`experience.table.${id}`),
        accessibilityLabelKey: tk('a11y.table.portfolioPerformance'),
        availability: 'AVAILABLE',
        owner: 'portfolio-management',
    };
}
function column(id, overrides = {}) {
    return {
        ...metadata(id, MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PERFORMANCE),
        labelKey: tk(`portfolioPerformance.columns.${id}`),
        field: id,
        fieldType: 'text',
        sortable: false,
        filterable: false,
        searchable: false,
        groupable: false,
        hideable: true,
        defaultVisible: true,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: 145,
        minWidth: 90,
        maxWidth: 320,
        align: 'left',
        nullDisplayBehavior: 'dash',
        ...overrides,
    };
}
export const PORTFOLIO_PERFORMANCE_TABLE = Object.freeze({
    ...metadata(PORTFOLIO_PERFORMANCE_TABLE_ID, MODULE_KEYS.PORTFOLIO_MGMT, FEATURE_KEYS.DATA_PERFORMANCE),
    operationId: OPERATION_IDS.DATA_PERFORMANCE_LIST,
    titleKey: tk('card.performance.title'),
    dataSourceId: PORTFOLIO_PERFORMANCE_QUERY_ID,
    rowIdField: 'id',
    columns: [
        column('portfolioReference', {
            searchable: true,
            requiredVisible: true,
            defaultWidth: 190,
        }),
        column('dpCode', { searchable: true, defaultWidth: 105 }),
        column('boIdMasked', { defaultWidth: 145 }),
        column('asOfDate', {
            fieldType: 'date',
            filterable: true,
            requiredVisible: true,
            defaultWidth: 130,
        }),
        column('asOfDateBs', { defaultWidth: 135 }),
        column('periodStart', {
            fieldType: 'date',
            filterable: true,
            defaultVisible: false,
            defaultWidth: 130,
        }),
        column('currency', { defaultWidth: 90, defaultVisible: false }),
        column('totalCost', {
            fieldType: 'money',
            requiredVisible: true,
            align: 'right',
        }),
        column('marketValue', {
            fieldType: 'money',
            requiredVisible: true,
            align: 'right',
        }),
        column('realizedGainLoss', { fieldType: 'money', align: 'right' }),
        column('unrealizedGainLoss', { fieldType: 'money', align: 'right' }),
        column('totalGainLoss', {
            fieldType: 'money',
            requiredVisible: true,
            align: 'right',
        }),
        column('returnOnCostPct', {
            fieldType: 'percentage',
            requiredVisible: true,
            align: 'right',
        }),
        column('benchmarkId', {
            filterable: true,
            defaultVisible: false,
            defaultWidth: 130,
        }),
        column('benchmarkReturnPct', {
            fieldType: 'percentage',
            align: 'right',
            defaultWidth: 145,
        }),
        column('activeReturnPct', {
            fieldType: 'percentage',
            align: 'right',
            defaultWidth: 135,
        }),
        column('benchmarkAsOf', {
            fieldType: 'date',
            defaultVisible: false,
            defaultWidth: 130,
        }),
        column('benchmarkStatus', {
            fieldType: 'status',
            defaultVisible: false,
            defaultWidth: 130,
        }),
        column('valuationCoveragePct', { fieldType: 'percentage', align: 'right' }),
        column('missingPriceCount', { fieldType: 'number', align: 'right' }),
        column('stalePriceCount', { fieldType: 'number', align: 'right' }),
        column('capitalGainTax', { fieldType: 'money', align: 'right' }),
        column('taxProvenanceCoveragePct', { fieldType: 'percentage', align: 'right' }),
        column('missingTaxProvenanceCount', { fieldType: 'number', align: 'right' }),
        column('nfrsEvidenceAvailable', { fieldType: 'boolean', defaultWidth: 140 }),
        column('nfrsFactCount', { fieldType: 'number', align: 'right' }),
        column('nfrsStandards', { defaultWidth: 180 }),
        column('nfrsMetrics', { defaultWidth: 180 }),
        column('partialData', { fieldType: 'boolean', defaultWidth: 110 }),
        column('methodology', { defaultWidth: 150 }),
        column('methodologyVersion', {
            defaultVisible: false,
            defaultWidth: 220,
        }),
        column('performanceReturnMethodology', { defaultWidth: 180 }),
        column('performanceReturnMethodologyVersion', {
            defaultVisible: false,
            defaultWidth: 210,
        }),
        column('performanceReturnNetTwr', {
            fieldType: 'percentage',
            align: 'right',
            defaultWidth: 145,
        }),
        column('performanceReturnGrossTwr', {
            fieldType: 'percentage',
            align: 'right',
            defaultWidth: 150,
        }),
        column('performanceReturnNetMwr', {
            fieldType: 'percentage',
            align: 'right',
            defaultWidth: 145,
        }),
        column('performanceReturnGrossMwr', {
            fieldType: 'percentage',
            align: 'right',
            defaultWidth: 150,
        }),
        column('performanceReturnInputDigest', {
            defaultVisible: false,
            defaultWidth: 220,
        }),
        column('performanceReturnLimitations', {
            defaultVisible: false,
            defaultWidth: 260,
        }),
        column('sourceBook', { defaultWidth: 210 }),
        column('sourceAsOf', {
            fieldType: 'datetime',
            defaultWidth: 180,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: false,
    serverSideFiltering: true,
    capabilities: {
        sorting: false,
        filtering: true,
        globalSearch: false,
        grouping: false,
        aggregation: false,
        pagination: true,
        selection: false,
        columnVisibility: true,
        columnResizing: true,
        columnReordering: true,
        columnPinning: false,
        savedViews: false,
        export: true,
        refresh: true,
        density: true,
        create: false,
    },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: false,
        showFilterRowToggle: true,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: false,
    },
    export: {
        formats: ['csv'],
        defaultFormat: 'csv',
        scopes: ['all'],
        defaultScope: 'all',
        formulaInjectionProtection: true,
        serverExport: {
            operationId: OPERATION_IDS.DATA_PERFORMANCE_EXPORT,
            endpoint: '/api/portfolio-performance/export',
            method: 'POST',
            filterParameterMap: {
                asOfDate: 'asOfDate',
                periodStart: 'periodStart',
                benchmarkId: 'benchmarkId',
            },
        },
    },
    privacyAndExportPolicy: {
        allowedExportFormats: ['csv'],
        auditExports: true,
    },
});
